import { useTranslation } from 'react-i18next'

import { cn } from '#/shared/lib/utils'
import type { DataTableWindow } from '#/shared/ui/data-table'
import { ChevronDownIcon } from '#/shared/ui/icons'
import { Popover } from '#/shared/ui/popover'

const PAGE_SIZE_OPTIONS = [10, 25, 50, 100]

interface TablePaginationProps {
  page: number
  pageSize: number
  totalItems: number
  totalPages: number
  onPageChange?: (page: number) => void
  onPageSizeChange?: (size: number) => void
  /**
   * The DataDropdown window. When present, the right-side pill shows the
   * absolute window range within the dataset and its arrows page the window
   * (1-100 -> 101-200). Absent (carrier, global search) → the pill mirrors the
   * sub-page position instead.
   */
  window?: DataTableWindow
  /**
   * Detached styling for the mobile card list: drops the card background/border
   * (the footer no longer caps a table) and nudges the controls down a touch.
   */
  bare?: boolean
}

function PageButton({
  page,
  active,
  onClick,
}: {
  page: number
  active: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex size-8 cursor-pointer items-center justify-center rounded-md border text-sm',
        active
          ? 'border-primary text-primary'
          : 'border-gray-200 text-gray-900',
      )}
    >
      {page}
    </button>
  )
}

function ChevronLeft(props: React.SVGProps<SVGSVGElement>) {
  return (
    <ChevronDownIcon {...props} className={cn('rotate-90', props.className)} />
  )
}

function ChevronRight(props: React.SVGProps<SVGSVGElement>) {
  return (
    <ChevronDownIcon {...props} className={cn('-rotate-90', props.className)} />
  )
}

function NavArrow({
  direction,
  disabled,
  onClick,
}: {
  direction: 'left' | 'right'
  disabled?: boolean
  onClick: () => void
}) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className="flex size-5 cursor-pointer items-center justify-center text-gray-900 disabled:opacity-30"
    >
      {direction === 'left' ? (
        <ChevronLeft className="size-2.5" />
      ) : (
        <ChevronRight className="size-2.5" />
      )}
    </button>
  )
}

function ShowingPill({
  from,
  to,
  total,
  prevDisabled,
  nextDisabled,
  onPrev,
  onNext,
}: {
  from: number
  to: number
  total: number
  prevDisabled: boolean
  nextDisabled: boolean
  onPrev: () => void
  onNext: () => void
}) {
  const { t } = useTranslation()
  return (
    <div className="flex h-8 items-center gap-1.25 rounded-md border border-gray-200 px-1.75">
      <span className="border-r border-gray-200 pr-2.5 text-sm">
        <span className="text-gray-300">{t('table.showing')}&nbsp;</span>
        <span className="text-gray-500">
          {from}-{to}
        </span>
        <span className="text-gray-300">
          &nbsp;{t('table.of')} {total.toLocaleString()}
        </span>
      </span>
      <NavArrow direction="left" disabled={prevDisabled} onClick={onPrev} />
      <NavArrow direction="right" disabled={nextDisabled} onClick={onNext} />
    </div>
  )
}

function PageSizeDropdown({
  pageSize,
  onPageSizeChange,
}: {
  pageSize: number
  onPageSizeChange?: (size: number) => void
}) {
  return (
    <Popover
      align="left"
      side="top"
      trigger={(open) => (
        <button
          type="button"
          className="flex h-8 cursor-pointer items-center gap-1 rounded-md border border-gray-200 px-1.75 text-sm text-gray-900"
        >
          {pageSize}
          <ChevronDownIcon
            className={cn(
              'size-2.5 transition-transform duration-200',
              open && 'rotate-180',
            )}
          />
        </button>
      )}
    >
      {(close) => (
        <div className="flex min-w-20 flex-col rounded-md border border-gray-200 bg-white py-1 shadow-popover">
          {PAGE_SIZE_OPTIONS.map((size) => (
            <button
              key={size}
              type="button"
              onClick={() => {
                onPageSizeChange?.(size)
                close()
              }}
              className={cn(
                'flex h-8 cursor-pointer items-center px-2.5 text-sm hover:bg-gray-100',
                size === pageSize ? 'text-primary' : 'text-gray-900',
              )}
            >
              {size}
            </button>
          ))}
        </div>
      )}
    </Popover>
  )
}

export function TablePagination({
  page,
  pageSize,
  totalItems,
  totalPages,
  onPageChange,
  onPageSizeChange,
  window: windowNav,
  bare,
}: TablePaginationProps) {
  const { t } = useTranslation()

  const from = (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)

  const visiblePages = getVisiblePages(page, totalPages)

  // The right pill defaults to the sub-page position, but when a window is
  // supplied it becomes the window navigator (1-100 -> 101-200, of dataset).
  const pill = windowNav
    ? {
        from: windowNav.from,
        to: windowNav.to,
        total: windowNav.total,
        prevDisabled: !windowNav.hasPrev,
        nextDisabled: !windowNav.hasNext,
        onPrev: () => windowNav.onPrev?.(),
        onNext: () => windowNav.onNext?.(),
      }
    : {
        from,
        to,
        total: totalItems,
        prevDisabled: page <= 1,
        nextDisabled: page >= totalPages,
        onPrev: () => onPageChange?.(page - 1),
        onNext: () => onPageChange?.(page + 1),
      }

  return (
    <div
      className={cn(
        'flex flex-wrap items-center justify-between gap-y-2.5 px-2.5 py-2.5',
        bare
          ? 'mt-2'
          : 'rounded-b-md border border-t-0 border-gray-200 bg-white',
      )}
    >
      <div className="flex items-center gap-3.75">
        <div className="flex items-center gap-2.5">
          <span className="hidden text-sm whitespace-nowrap text-gray-300 sm:inline">
            {t('table.showPerPage')}
          </span>
          <PageSizeDropdown
            pageSize={pageSize}
            onPageSizeChange={onPageSizeChange}
          />
        </div>

        <div className="flex items-center gap-2.5">
          <NavArrow
            direction="left"
            disabled={page <= 1}
            onClick={() => onPageChange?.(page - 1)}
          />
          {visiblePages.map((p) => (
            <PageButton
              key={p}
              page={p}
              active={p === page}
              onClick={() => onPageChange?.(p)}
            />
          ))}
          <NavArrow
            direction="right"
            disabled={page >= totalPages}
            onClick={() => onPageChange?.(page + 1)}
          />
        </div>
      </div>

      <ShowingPill {...pill} />
    </div>
  )
}

function getVisiblePages(current: number, total: number): number[] {
  const maxVisible = 4
  let start = Math.max(1, current - 1)
  const end = Math.min(total, start + maxVisible - 1)
  start = Math.max(1, end - maxVisible + 1)

  const pages: number[] = []
  for (let i = start; i <= end; i++) pages.push(i)
  return pages
}
